import type { EditorEvents, Extensions } from "@tiptap/core";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";
import type { RichTextEditorRef } from "mui-tiptap";
import { LinkBubbleMenu, LinkBubbleMenuHandler, RichTextEditor } from "mui-tiptap";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import DefaultHtmlEditorControls from "./DefaultHtmlEditorControls";
import HtmlEditorImageExtension from "./htmlEditorExtensions/HtmlEditorImageExtension";
import type { HtmlEditorUploadedImage } from "./htmlEditorTypes";

const baseEditorExtensions: Extensions = [
  StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5] } }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  LinkBubbleMenuHandler,
];

// TextAlign throws on setTextAlign for a type missing from the schema, so "image" is listed only with the extension.
const editorExtensions: Extensions = [
  ...baseEditorExtensions,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
];

const editorExtensionsWithImages: Extensions = [
  ...baseEditorExtensions,
  TextAlign.configure({ types: ["heading", "paragraph", "image"] }),
  HtmlEditorImageExtension,
];

interface Props {
  initialContent: string;
  onChange: (content: string) => void;
  onUploadImages?: (files: File[]) => Promise<HtmlEditorUploadedImage[]>;
  menuControls?: () => ReactNode;
  focusOnInit?: boolean;
  readOnly?: boolean;
  error?: boolean;
  editorStyles?: CSSProperties;
}

const HtmlEditor = ({
  initialContent,
  onChange,
  onUploadImages,
  menuControls,
  focusOnInit,
  readOnly,
  error,
  editorStyles,
}: Props) => {
  const rteRef = useRef<RichTextEditorRef>(null);

  useEffect(() => {
    if (focusOnInit) {
      rteRef.current?.editor?.commands.focus();
    }
  }, [focusOnInit]);

  const handleUpdate = useCallback(
    (e: EditorEvents["update"]) => {
      // Toggling `editable` also emits an update, without a document change.
      if (e.transaction.docChanged) {
        onChange(e.editor.getHTML());
      }
    },
    [onChange]
  );

  return (
    <RichTextEditor
      ref={rteRef}
      extensions={onUploadImages ? editorExtensionsWithImages : editorExtensions}
      content={initialContent}
      editable={!readOnly}
      renderControls={menuControls ?? (() => <DefaultHtmlEditorControls onUploadImages={onUploadImages} />)}
      onUpdate={handleUpdate}
      sx={{
        "& .MuiTiptap-RichTextContent-root": {
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          ...editorStyles,
        },
        "& .ProseMirror": { flex: 1 },
        ...(error && {
          "& .MuiTiptap-FieldContainer-notchedOutline, &:hover .MuiTiptap-FieldContainer-notchedOutline": {
            borderColor: "error.main",
          },
        }),
      }}
    >
      {() => (
        <>
          <LinkBubbleMenu />
        </>
      )}
    </RichTextEditor>
  );
};

export default HtmlEditor;
